import React from 'react';
import type { OnboardingAnswers } from '../../../../shared/types/accounts';

interface Props {
  repoCount: OnboardingAnswers['repoCount'];
  orgCount: OnboardingAnswers['orgCount'];
  projectCount: OnboardingAnswers['projectCount'];
  onChange: (field: 'repoCount' | 'orgCount' | 'projectCount', value: string) => void;
}

function RadioGroup<T extends string>({ label, value, options, onChange }: {
  label: string; value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (v: T) => void;
}) {
  return (
    <div>
      <p className="text-xs font-medium text-slate-700 mb-2">{label}</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {options.map((opt) => (
          <label
            key={opt.value}
            className={`flex items-center justify-center px-3 py-2 rounded-lg border text-sm font-medium cursor-pointer transition-colors ${
              value === opt.value
                ? 'bg-primary-50 border-primary-500 text-primary-700'
                : 'bg-white border-slate-300 text-slate-700 hover:border-slate-400'
            }`}
          >
            <input
              type="radio"
              name={label}
              value={opt.value}
              checked={value === opt.value}
              onChange={() => onChange(opt.value)}
              className="sr-only"
            />
            {opt.label}
          </label>
        ))}
      </div>
    </div>
  );
}

/**
 * UX-08: body-only — the three radio groups from the old `StepScale`
 * step, minus its own Back/Continue footer.
 */
export const ScaleFields: React.FC<Props> = ({ repoCount, orgCount, projectCount, onChange }) => (
  <div className="space-y-4">
    <RadioGroup
      label="How many repos will you connect?"
      value={repoCount}
      options={[{ value: '1', label: '1' }, { value: '2-3', label: '2–3' }, { value: '4-15', label: '4–15' }, { value: '15+', label: '15+' }]}
      onChange={(v) => onChange('repoCount', v)}
    />
    <RadioGroup
      label="How many GitHub orgs?"
      value={orgCount}
      options={[{ value: '1', label: '1' }, { value: '2+', label: '2+' }]}
      onChange={(v) => onChange('orgCount', v)}
    />
    <RadioGroup
      label="How many products / projects?"
      value={projectCount}
      options={[{ value: '1', label: '1' }, { value: '2-3', label: '2–3' }, { value: '4-10', label: '4–10' }, { value: '10+', label: '10+' }]}
      onChange={(v) => onChange('projectCount', v)}
    />
  </div>
);
